import { __decorate } from "tslib";
import { Module, VuexModule, MutationAction } from 'vuex-module-decorators';
import baseCharacter from './CharacterEngine/baseCharacter.json';
import { findIndex, isEmpty, isEqual, debounce } from 'lodash';
import generateCharacter from './CharacterEngine/generateCharacter';
import builderVersion from '@/version';
import axios from 'axios';
const abilityScores = [
    'Strength',
    'Dexterity',
    'Constitution',
    'Intelligence',
    'Wisdom',
    'Charisma'
];
function stateOf(context) {
    // Vuex-module-decorator changes 'this' when it converts into a module.
    return context.state;
}
function rootOf(myThis) {
    return myThis;
}
const saveCharacterToDB = debounce(async (newCharacter, header, myThis) => {
    const characterResult = (await axios.post(`${process.env.VUE_APP_sw5eapiurl}/api/character`, {
        jsonData: JSON.stringify(newCharacter),
        id: newCharacter.id
    }, header)).data;
    myThis.dispatch('saveCharacterLocally', {
        ...JSON.parse(characterResult.jsonData),
        userId: characterResult.userId,
        id: characterResult.id
    });
}, 1000);
const updateCharacterList = (characters, newCharacter) => {
    let index = findIndex(characters, ({ id, localId }) => id && newCharacter.id
        ? newCharacter.id === id
        : localId && newCharacter.localId ? newCharacter.localId === localId : false);
    if (index < 0)
        index = characters.length;
    characters.splice(index, 1, newCharacter);
    return characters;
};
let Character = class Character extends VuexModule {
    constructor() {
        super(...arguments);
        this.characters = [];
    }
    get authenticationHeader() {
        return async (myThis) => {
            await myThis.dispatch('authentication/fetchAccessToken', {}, { root: 'true' });
            return myThis.rootGetters['authentication/axiosHeader'];
        };
    }
    get getCharacterById() {
        return (characterId) => {
            const characters = stateOf(this.context).characters;
            return characters.find(({ id }) => id === characterId) ||
                characters.find(({ localId }) => localId === characterId);
        };
    }
    get getIsEmptyCharacter() {
        return (character) => {
            if (!character)
                return true;
            const { id, userId, localId, builderVersion, ...characterDetails } = character;
            return isEmpty(character) || isEqual({ ...baseCharacter, ...characterDetails }, baseCharacter);
        };
    }
    get generateCompleteCharacter() {
        return (rawCharacter) => {
            if (this.getCharacterValidation(rawCharacter).code === 0) {
                try {
                    const rootState = this.context.rootState;
                    return generateCharacter(rawCharacter, rootState.classes.classes, rootState.archetypes.archetypes, rootState.species.species, rootState.equipment.equipment, rootState.enhancedItems.enhancedItems, rootState.powers.powers, rootState.feats.feats, rootState.features.features, rootState.backgrounds.backgrounds, rootState.characterAdvancements.characterAdvancements, rootState.skills.skills, rootState.conditions.conditions);
                }
                catch (e) {
                    console.error('Character Generation failed. Character built with builder version ' + rawCharacter.builderVersion);
                    console.error(e);
                    return null;
                }
            }
        };
    }
    get getCharacterValidation() {
        return (character) => {
            if (!character)
                return { code: 1, message: 'No Character Found', isValid: false };
            return [
                { message: 'No character found', isValid: !isEmpty(character) },
                { message: 'Missing a name', isValid: character.name !== '' },
                { message: 'Missing a species', isValid: character.species && character.species.name !== '' },
                { message: 'Missing class levels', isValid: character.classes && character.classes.length > 0 },
                {
                    message: 'Missing hit points for a class',
                    isValid: character.classes && character.classes.every((myClass, index) => myClass.hitPoints && myClass.hitPoints.length === myClass.levels - (!index ? 1 : 0))
                },
                {
                    message: 'Missing an ability score',
                    isValid: character.baseAbilityScores &&
                        isEqual(Object.keys(character.baseAbilityScores).sort(), abilityScores.sort()) &&
                        Object.values(character.baseAbilityScores).every(score => score > 0)
                },
                { message: 'Missing a background', isValid: character.background && character.background.name !== '' }
            ]
                .map((validation, index) => ({ code: index + 1, ...validation }))
                .find(({ isValid }) => !isValid) || { code: 0, message: 'All checks passed', isValid: true };
        };
    }
    async saveCharacter(newCharacter) {
        newCharacter = { ...newCharacter, builderVersion, changedAt: Date.now() };
        if (rootOf(this).rootGetters['authentication/isLoggedIn']) {
            const header = await this.getters.authenticationHeader(this);
            saveCharacterToDB(newCharacter, header, this);
        }
        return { characters: updateCharacterList(stateOf(this).characters, newCharacter) };
    }
    async saveCharacters(newCharacters) {
        let characters = stateOf(this).characters;
        const header = await this.getters.authenticationHeader(this);
        if (newCharacters.length && rootOf(this).rootGetters['authentication/isLoggedIn']) {
            const response = await axios.post(`${process.env.VUE_APP_sw5eapiurl}/api/character/multiple`, {
                characterRequests: newCharacters.map(character => ({
                    jsonData: JSON.stringify(character),
                    id: character.id
                }))
            }, header);
            response.data.forEach((characterResult) => updateCharacterList(characters, {
                ...JSON.parse(characterResult.jsonData),
                userId: characterResult.userId,
                id: characterResult.id
            }));
        }
        return { characters };
    }
    async saveCharacterLocally(newCharacter) {
        return { characters: updateCharacterList(stateOf(this).characters, {
                ...newCharacter,
                builderVersion,
                changedAt: Date.now()
            }) };
    }
    async fetchCharacters() {
        if (rootOf(this).rootGetters['authentication/isLoggedIn']) {
            const header = await this.getters.authenticationHeader(this);
            const characterResults = (await axios.get(`${process.env.VUE_APP_sw5eapiurl}/api/character`, header)).data;
            return { characters: [
                    ...characterResults.map(({ id, userId, jsonData }) => ({
                        ...JSON.parse(jsonData),
                        id,
                        userId
                    })),
                    ...stateOf(this).characters.filter(({ id }) => !id)
                ].slice(0, 20) };
        }
        else {
            return { characters: stateOf(this).characters };
        }
    }
    async deleteCharacter(character) {
        if (rootOf(this).rootGetters['authentication/isLoggedIn']) {
            const header = await this.getters.authenticationHeader(this);
            await axios.delete(`${process.env.VUE_APP_sw5eapiurl}/api/character/${character.id}`, header);
        }
        return { characters: stateOf(this).characters.filter(({ localId, id }) => localId !== character.localId && id !== character.id) };
    }
    async clearLocalCharacters() {
        return { characters: [] };
    }
};
__decorate([
    MutationAction({ mutate: ['characters'] })
], Character.prototype, "saveCharacter", null);
__decorate([
    MutationAction({ mutate: ['characters'] })
], Character.prototype, "saveCharacters", null);
__decorate([
    MutationAction({ mutate: ['characters'] })
], Character.prototype, "saveCharacterLocally", null);
__decorate([
    MutationAction({ mutate: ['characters'] })
], Character.prototype, "fetchCharacters", null);
__decorate([
    MutationAction({ mutate: ['characters'] })
], Character.prototype, "deleteCharacter", null);
__decorate([
    MutationAction({ mutate: ['characters'] })
], Character.prototype, "clearLocalCharacters", null);
Character = __decorate([
    Module({ namespaced: true, name: 'character' })
], Character);
export default Character;
